import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config";
import { Activity, CalendarDays, CreditCard, FileText, HardDrive, RefreshCw, Wrench } from "lucide-react";

const KIND_STYLE = {
  booking: { Icon: CalendarDays, cls: "bg-brand text-topacio-ink" },
  payment: { Icon: CreditCard, cls: "bg-topacio-green text-white" },
  fleet: { Icon: Wrench, cls: "bg-topacio-red text-white" },
  docs: { Icon: FileText, cls: "bg-white text-topacio-ink" },
};

const SOURCE_LABELS = { backend: "Backend (app)", backup: "Backup diario", health: "Monitor de salud" };

function fmtTs(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function LogsTab({ headers }) {
  const [sources, setSources] = useState([]);
  const [events, setEvents] = useState([]);
  const [active, setActive] = useState("backend");
  const [lines, setLines] = useState([]);
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const [{ data: l }, { data: a }] = await Promise.all([
        axios.get(`${API}/admin/logs`, { headers }),
        axios.get(`${API}/admin/logs/activity`, { headers }),
      ]);
      setSources(l.logs);
      setEvents(a.events);
    } catch (e) {
      setErr(e.response?.status === 403 ? "Solo el superadministrador puede ver los registros." : "Error cargando registros");
    }
  }, [headers]);

  const loadTail = useCallback(async (name) => {
    try {
      const { data } = await axios.get(`${API}/admin/logs/tail`, { headers, params: { name, lines: 300 } });
      setLines(data.lines);
      setMeta(data);
    } catch {
      setLines([]);
      setMeta(null);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTail(active); }, [active, loadTail]);

  return (
    <div data-testid="logs-tab" className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <section className="card-brutal p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display font-bold text-topacio-ink flex items-center gap-2">
            <Activity className="w-4 h-4" /> Actividad de la aplicación
          </h3>
          <button data-testid="logs-refresh-btn" onClick={load} className="text-topacio-faded hover:text-topacio-ink transition-colors">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        {err && <p data-testid="logs-error" className="text-sm font-bold text-topacio-red">{err}</p>}
        <div className="space-y-2.5 max-h-[560px] overflow-y-auto pr-1">
          {events.map((e, i) => {
            const { Icon, cls } = KIND_STYLE[e.kind] || KIND_STYLE.docs;
            return (
              <div key={i} data-testid={`log-event-${i}`} className="flex items-start gap-3">
                <span className={`mt-0.5 w-7 h-7 shrink-0 rounded-lg border-2 border-topacio-ink flex items-center justify-center ${cls}`}>
                  <Icon className="w-3.5 h-3.5" />
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-topacio-ink leading-snug">{e.text}</p>
                  <p className="text-[10px] text-topacio-faded font-semibold mt-0.5">{fmtTs(e.at)}</p>
                </div>
              </div>
            );
          })}
          {!events.length && !err && <p className="text-sm text-topacio-faded">Sin actividad registrada todavía.</p>}
        </div>
      </section>

      <section className="card-brutal p-6">
        <h3 className="font-display font-bold text-topacio-ink flex items-center gap-2 mb-5">
          <HardDrive className="w-4 h-4" /> Archivos de log del servidor
        </h3>
        <div className="flex flex-wrap gap-2 mb-4">
          {sources.map((s) => (
            <button key={s.name} data-testid={`log-source-${s.name}`} onClick={() => setActive(s.name)}
              className={`px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-wider rounded-lg border-2 border-topacio-ink transition-colors ${active === s.name ? "bg-topacio-ink text-brand" : "bg-white text-topacio-faded"}`}>
              {SOURCE_LABELS[s.name] || s.name}
              <span className="ml-1.5 font-mono normal-case opacity-70">{s.size_kb != null ? `${s.size_kb} KB` : "—"}</span>
            </button>
          ))}
        </div>
        {meta ? (
          <pre data-testid="log-viewer" className="bg-topacio-ink text-emerald-300 text-[11px] leading-relaxed rounded-xl p-4 max-h-[480px] overflow-auto font-mono whitespace-pre-wrap break-all">
            {lines.length ? lines.join("\n") : "(archivo vacío)"}
          </pre>
        ) : (
          <p data-testid="log-viewer-empty" className="text-sm text-topacio-faded">
            Este log aún no existe en el servidor. Se crea solo cuando el servicio correspondiente escribe.
          </p>
        )}
        <button data-testid="log-tail-refresh" onClick={() => loadTail(active)}
          className="btn-secondary !py-2 !text-[11px] mt-4">
          <RefreshCw className="w-3.5 h-3.5" /> Recargar últimas 300 líneas
        </button>
      </section>
    </div>
  );
}
